import React from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import { sanitizeHtml } from "./markdown";
import { decodeEntities } from "./decodeEntities";

const STEP_META = {
  email: { icon: "mail", label: "Email" },
  linkedin: { icon: "person_add", label: "LinkedIn" },
  // Classic-set glyph names only — the pinned Material Icons font predates
  // task_alt/travel_explore etc., which render as empty boxes.
  call: { icon: "call", label: "Call" },
  custom_task: { icon: "assignment", label: "Task" },
  deep_research: { icon: "search", label: "Deep Research" }
};

const linkedinActionLabel = action =>
  ({
    view_profile: "View profile",
    connection_request: "Connection request",
    message: "Message",
    inmail: "InMail",
    interact_with_post: "Interact with a post"
  }[action] || "LinkedIn task");

const StepCard = ({ step, index, isNewest, building }) => {
  const meta = STEP_META[step.type] || STEP_META.email;
  const dayLabel = index === 0 ? "Day 1" : `+${step.days_later || 0} day${(step.days_later || 0) === 1 ? "" : "s"}`;
  return (
    <div className={`copilotSeqStep ${isNewest && building ? "entering" : ""}`}>
      <div className="copilotSeqStep__rail">
        <span className="copilotSeqStep__num">{index + 1}</span>
        <span className="copilotSeqStep__day">{dayLabel}</span>
      </div>
      <div className="copilotSeqStep__card">
        <div className="copilotSeqStep__head">
          <i className="material-icons-outlined">{meta.icon}</i>
          <span className="copilotSeqStep__channel">{meta.label}</span>
          {step.type === "linkedin" && (
            <span className="copilotSeqStep__liAction">
              {linkedinActionLabel(step.linkedin_action)}
              {step.automated === false ? " · manual task" : ""}
            </span>
          )}
          {step.variantCount > 1 && (
            <span className="copilotSeqStep__liAction">
              {`${step.variantCount} variants · showing 1`}
            </span>
          )}
          {step.type !== "linkedin" && step.automated === false && (
            <span className="copilotSeqStep__liAction">manual task</span>
          )}
          {step.aiGenerated && (
            <span className="copilotSeqStep__aiBadge">AI written</span>
          )}
          {typeof step.contacts === "number" && (
            <span className="copilotSeqStep__liAction">
              {`${step.contacts.toLocaleString("en-US")} contact${
                step.contacts === 1 ? "" : "s"
              }`}
            </span>
          )}
        </div>
        {step.subject && (
          <div className="copilotSeqStep__subject">{step.subject}</div>
        )}
        {step.body && (
          <div
            className="copilotSeqStep__body"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(step.body) }}
          />
        )}
        {step.message && (
          <div className="copilotSeqStep__body">{step.message}</div>
        )}
        {step.notes && <div className="copilotSeqStep__body">{step.notes}</div>}
        {/* An AI step legitimately has no copy yet — say so rather than
            rendering a card that looks like it failed to load. */}
        {!step.subject && !step.body && !step.message && !step.notes && (
          <div className="copilotSeqStep__empty">
            {step.aiGenerated
              ? "Written per contact at send time"
              : "No content on this step"}
          </div>
        )}
      </div>
    </div>
  );
};

const SequencePreviewPanel = observer(() => {
  const block = copilotStore.activePreviewBlock;
  if (!block) return null;
  const building = block.status === "building";
  const steps = block.steps || [];

  return (
    <>
      <div className="copilotTablePanel copilotPreviewPanel">
        <div className="copilotTablePanel__header">
          <div className="copilotTablePanel__headMeta">
            <div className="copilotTablePanel__title">
              {building && <span className="copilotLiveCard__liveDot" />}
              {decodeEntities(block.title)}
            </div>
            <div className="copilotTablePanel__count">
              {/* Viewing an existing sequence is not a creation: the build
                  wording belongs to the agent's live build only. */}
              {block.sequenceId
                ? building
                  ? "Loading steps…"
                  : block.status === "failed"
                  ? "Couldn't load this sequence"
                  : `${steps.length} step${steps.length === 1 ? "" : "s"}`
                : building
                ? "Building in realtime…"
                : block.status === "failed"
                ? "Creation failed"
                : `Created · ${steps.length} steps`}
            </div>
          </div>
          <span
            className="material-icons-outlined copilotTablePanel__close"
            onClick={copilotStore.closePreviewPanel}
          >
            close
          </span>
        </div>
        <div className="copilotPreviewPanel__banner">
          <i className="material-icons-outlined">visibility</i>
          <span>
            Live preview, view only. Actions happen from the chat, this panel
            is not editable.
          </span>
        </div>
        {/* pointer-events disabled in SCSS: nothing here is clickable */}
        <div className="copilotTablePanel__body copilotPreviewPanel__body">
          <div className="copilotSeqPreview">
            {steps.map((step, index) => (
              <StepCard
                key={`step_${index}`}
                step={step}
                index={index}
                isNewest={index === steps.length - 1}
                building={building}
              />
            ))}
            {building && (
              <div className="copilotSeqPreview__typing">
                <span className="copilotDot" />
                <span className="copilotDot" />
                <span className="copilotDot" />
                <span className="copilotSeqPreview__typingLabel">
                  writing the next step…
                </span>
              </div>
            )}
            {!building && block.status === "done" && (
              <div className="copilotSeqPreview__done">
                <i className="material-icons-outlined">check_circle</i>
                <span>
                  Draft saved{block.sequenceId ? ` (ID: ${block.sequenceId})` : ""}.
                  Manage it from Sequences, or keep going in the chat.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

export default SequencePreviewPanel;
